// src/screens/admin/CotizadorCapturas.jsx
// Botón "Llenar con capturas" de una fila del Cotizador. Subes o fotografías 1 a 6 capturas
// (Comprarviagem, Speedmilhas, Resumo do pedido, Buscamilhas), la IA las lee, y antes de tocar nada
// te muestra "Esto leí" para que lo confirmes. Capturas del mismo vuelo se juntan en una fila;
// vuelos distintos van a filas distintas. Lo que no se lee con certeza queda vacío: nada se inventa.
import React, { useState, useRef, useEffect } from 'react';
import { toast } from '../../components/UI';
import { supabase } from '../../services/supabase';
import { fileToBase64Jpeg } from '../../services/imageGenService';
import { agruparLecturas, planDeFilas } from '../../services/capturasVuelo';
import { fmtBRL } from '../../services/cotizadorCalc';
import { btnChico } from './CotizadorUI';

const EXTRACT_URL = '/.netlify/functions/extract-flight-cotizador';
const MAX_CAPTURAS = 6;

async function leerCaptura(file, token) {
  const { base64, mediaType } = await fileToBase64Jpeg(file, 1600, 0.85);
  const res = await fetch(EXTRACT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ image: base64, mediaType }),
  });
  const d = await res.json().catch(() => ({}));
  if (res.status === 403) throw new Error('403');
  return res.ok ? d : { error: d.error || 'No se pudo leer.' };
}

const overlay = { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' };
const hoja = { background: 'var(--bg)', width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', padding: 16, boxSizing: 'border-box' };

export default function CotizadorCapturas({ op, onAplicar }) {
  const [abierto, setAbierto] = useState(false); // hoja "Cargar capturas" (cámara / galería)
  const [pendientes, setPendientes] = useState([]); // [{ file, url }]
  const [leyendo, setLeyendo] = useState('');
  const [plan, setPlan] = useState(null);
  const [sinLeer, setSinLeer] = useState([]);
  const [aplicando, setAplicando] = useState(false);
  const camara = useRef(null);
  const galeria = useRef(null);
  const urls = useRef([]);

  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const agregar = (files) => {
    const nuevos = files.slice(0, Math.max(0, MAX_CAPTURAS - pendientes.length));
    if (files.length > nuevos.length) toast(`Máximo ${MAX_CAPTURAS} capturas a la vez.`, 'error');
    const conUrl = nuevos.map((file) => { const url = URL.createObjectURL(file); urls.current.push(url); return { file, url }; });
    setPendientes((p) => [...p, ...conUrl]);
  };
  const tomada = (e) => { agregar([...(e.target.files || [])]); e.target.value = ''; };
  const quitar = (i) => setPendientes((p) => p.filter((_, j) => j !== i));
  const cerrar = () => { setAbierto(false); setPendientes([]); };

  const leer = async () => {
    if (!pendientes.length) return;
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token || '';
      let hechas = 0;
      setLeyendo(`Leyendo 0 de ${pendientes.length}…`);
      const lecturas = await Promise.all(pendientes.map(async ({ file }) => {
        const l = await leerCaptura(file, token).catch((err) => (err.message === '403' ? { error: 'No autorizado.' } : { error: 'No se pudo leer.' }));
        hechas += 1;
        setLeyendo(`Leyendo ${hechas} de ${pendientes.length}…`);
        return l;
      }));
      const { grupos, sinLeer: malas } = agruparLecturas(lecturas);
      if (!grupos.length) { toast('No pude leer ningún vuelo en esas capturas. Prueba con otra foto o llénalo a mano.', 'error'); setLeyendo(''); return; }
      setSinLeer(malas);
      setPlan(planDeFilas(grupos, op));
      cerrar();
    } catch { toast('No se pudieron leer las capturas.', 'error'); }
    setLeyendo('');
  };

  const aplicar = async () => {
    setAplicando(true);
    await onAplicar(plan);
    setAplicando(false);
    setPlan(null);
  };

  return (
    <>
      <button type="button" onClick={() => setAbierto(true)} disabled={!!leyendo} style={{ ...btnChico, color: 'var(--orange)', borderColor: 'var(--orange)', fontWeight: 700 }}>
        {leyendo || '📷 Llenar con capturas'}
      </button>
      {/* La cámara toma una foto cada vez; la galería deja elegir varias. Ambas suman a la misma lista. */}
      <input ref={camara} type="file" accept="image/*" capture="environment" onChange={tomada} style={{ display: 'none' }} />
      <input ref={galeria} type="file" accept="image/*" multiple onChange={tomada} style={{ display: 'none' }} />

      {abierto && (
        <div role="dialog" aria-modal="true" aria-label="Cargar capturas" style={overlay}>
          <div style={hoja}>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--white)', margin: '0 0 4px' }}>Cargar capturas</h3>
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.45 }}>
              Toma una foto de la pantalla o elige capturas de tu galería (hasta {MAX_CAPTURAS}). Pueden ser de sitios distintos y del mismo vuelo.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <button type="button" onClick={() => camara.current?.click()} disabled={pendientes.length >= MAX_CAPTURAS} style={{ ...btnChico, padding: '14px 0', fontSize: 14, fontWeight: 700, color: 'var(--orange)', borderColor: 'var(--orange)' }}>📷 Tomar foto</button>
              <button type="button" onClick={() => galeria.current?.click()} disabled={pendientes.length >= MAX_CAPTURAS} style={{ ...btnChico, padding: '14px 0', fontSize: 14, fontWeight: 700 }}>🖼 Galería</button>
            </div>

            {pendientes.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {pendientes.map((p, i) => (
                  <div key={p.url} style={{ position: 'relative', width: 72, height: 72 }}>
                    <img src={p.url} alt={`Captura ${i + 1}`} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--navy-border)' }} />
                    <button type="button" aria-label={`Quitar la captura ${i + 1}`} onClick={() => quitar(i)} style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 11, border: 'none', background: 'var(--error)', color: '#fff', fontSize: 13, lineHeight: '22px', padding: 0 }}>×</button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-primary" disabled={!pendientes.length || !!leyendo} onClick={leer} style={{ flex: 2, fontSize: 14 }}>
                {leyendo || (pendientes.length ? `Leer ${pendientes.length} ${pendientes.length === 1 ? 'captura' : 'capturas'}` : 'Agrega una captura')}
              </button>
              <button type="button" disabled={!!leyendo} onClick={cerrar} style={{ ...btnChico, flex: 1, fontSize: 13 }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {plan && (
        <div role="dialog" aria-modal="true" aria-label="Esto leí" style={overlay}>
          <div style={hoja}>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--white)', margin: '0 0 4px' }}>Esto leí</h3>
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.45 }}>
              Revisa que coincida con tus capturas. Lo que no se pudo leer queda vacío y lo completas a mano.
            </p>

            {plan.map((p, i) => (
              <div key={p.g.clave} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 12, marginBottom: 10 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--orange)', margin: '0 0 4px' }}>
                  VUELO {i + 1} · {p.enActual ? 'va en esta fila' : p.patch.dia_vuelo ? `fila nueva del ${p.patch.dia_vuelo.slice(8)}/${p.patch.dia_vuelo.slice(5, 7)}` : 'fila nueva debajo (esta fila ya tiene un vuelo)'}
                </p>
                {p.resumen.map((t) => <p key={t} style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', margin: '0 0 2px' }}>{t}</p>)}
                {p.precios.length > 0 ? p.precios.map((pr, k) => (
                  <p key={`${pr.fuente}-${k}`} style={{ fontSize: 13, color: 'var(--off-white)', margin: '2px 0 0' }}>
                    {pr.fuente}: <b>{pr.valor !== null ? `R$ ${fmtBRL(pr.valor)}` : `${pr.millas.toLocaleString('pt-BR')} millas`}</b>
                  </p>
                )) : <p style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0 0' }}>Sin precio leído.</p>}
                {p.reemplazos.map((r) => (
                  <p key={r.campo} style={{ fontSize: 12, color: 'var(--gold)', margin: '6px 0 0', lineHeight: 1.4 }}>
                    ↻ Reemplaza {r.nombre}: R$ {fmtBRL(r.antes)} → R$ {fmtBRL(r.despues)}
                  </p>
                ))}
                {p.conservados.map((c) => (
                  <p key={c.campo} style={{ fontSize: 12, color: 'var(--muted)', margin: '6px 0 0', lineHeight: 1.4 }}>
                    Se conserva {c.nombre}: R$ {fmtBRL(c.valor)} (no vino en las capturas; si era de otro vuelo, bórralo)
                  </p>
                ))}
                {p.avisos.map((a) => <p key={a} style={{ fontSize: 12, color: 'var(--gold)', margin: '6px 0 0', lineHeight: 1.4 }}>⚠ {a}</p>)}
              </div>
            ))}
            {sinLeer.length > 0 && (
              <p style={{ fontSize: 12, color: 'var(--gold)', margin: '0 0 10px' }}>
                No pude leer {sinLeer.length === 1 ? 'la captura' : 'las capturas'} {sinLeer.join(', ')}.
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button type="button" className="btn-primary" disabled={aplicando} onClick={aplicar} style={{ flex: 2, fontSize: 14 }}>
                {aplicando ? 'Cargando…' : `Cargar ${plan.length} ${plan.length === 1 ? 'vuelo' : 'vuelos'}`}
              </button>
              <button type="button" disabled={aplicando} onClick={() => setPlan(null)} style={{ ...btnChico, flex: 1, fontSize: 13 }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
